import type { Client, Task, RunSummary, GrowthList, LogEntry, User, AuthResponse, ApiCredentialMetadata } from './types'

const BASE_URL = '/api'

let authToken: string | null = localStorage.getItem('auth_token')

export function setAuthToken(token: string | null) {
  authToken = token
  if (token) {
    localStorage.setItem('auth_token', token)
  } else {
    localStorage.removeItem('auth_token')
  }
}

export function getAuthToken(): string | null {
  return authToken
}

async function fetchJSON<T>(url: string, options?: RequestInit): Promise<T> {
  const headers = new Headers(options?.headers)
  headers.set('Content-Type', 'application/json')

  if (authToken) {
    headers.set('Authorization', `Bearer ${authToken}`)
  }

  const res = await fetch(`${BASE_URL}${url}`, {
    ...options,
    credentials: 'include',
    headers,
  })

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ error: res.statusText }))

    if (res.status === 401) {
      setAuthToken(null)
      window.dispatchEvent(new Event('auth:unauthorized'))
    }

    throw new Error(errorData.error || `HTTP ${res.status}`)
  }

  return res.json()
}

export const api = {
  // Authentication
  login: async (email: string, password: string) => {
    const response = await fetchJSON<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
    setAuthToken(response.token)
    return response
  },
  logout: async () => {
    await fetchJSON<{ message: string }>('/auth/logout', { method: 'POST' })
    setAuthToken(null)
  },
  getCurrentUser: () => fetchJSON<{ user: User }>('/auth/me'),
  refreshToken: async () => {
    const response = await fetchJSON<AuthResponse>('/auth/refresh', { method: 'POST' })
    setAuthToken(response.token)
    return response
  },

  // User Management (Admin only)
  getUsers: () => fetchJSON<{ users: User[] }>('/users'),
  createUser: (data: { email: string; password: string; name: string; role?: 'admin' | 'user' }) =>
    fetchJSON<{ user: User }>('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateUser: (id: number, data: { email?: string; name?: string; role?: 'admin' | 'user'; password?: string }) =>
    fetchJSON<{ user: User }>(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteUser: (id: number) =>
    fetchJSON<{ message: string }>(`/users/${id}`, {
      method: 'DELETE',
    }),

  // Activity Logs & Settings (Admin only)
  getActivityLogs: (filters?: { userId?: number; action?: string; limit?: number }) =>
    fetchJSON<{ logs: any[] }>('/activity' + (filters ? `?${new URLSearchParams(filters as any)}` : '')),
  getCredentials: () => fetchJSON<{ credentials: ApiCredentialMetadata[] }>('/credentials'),
  updateCredential: (data: { service: string; apiKey: string; baseUrl?: string }) =>
    fetchJSON<{ message: string; credentials?: ApiCredentialMetadata[]; restart_recommended?: boolean }>('/credentials', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Clients
  getClients: () => fetchJSON<{ clients: Client[] }>('/clients'),
  getClient: (id: string) => fetchJSON<{ client: Client }>(`/clients/${id}`),
  createClient: (data: { name: string; linkedin_url?: string; unipile_account_id: string }) =>
    fetchJSON<{ client: Client }>('/clients', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateClient: (id: string, data: { name?: string; linkedin_url?: string; unipile_account_id?: string }) =>
    fetchJSON<{ client: Client }>(`/clients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteClient: (id: string) =>
    fetchJSON<{ message: string }>(`/clients/${id}`, {
      method: 'DELETE',
    }),

  // Tasks
  getTasks: () => fetchJSON<{ tasks: Task[] }>('/tasks'),
  getTask: (id: string) => fetchJSON<Task>(`/tasks/${id}`),
  getTaskHistory: (id: string) => fetchJSON<{ runs: any[] }>(`/tasks/${id}/history`),
  createTask: (data: Partial<Task>) =>
    fetchJSON<Task>('/tasks', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateTask: (id: string, data: Partial<Task>) =>
    fetchJSON<Task>(`/tasks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteTask: (id: string) =>
    fetchJSON<{ removed: Task }>(`/tasks/${id}`, {
      method: 'DELETE',
    }),

  // Pipeline control
  runTask: (id: string, overrides?: { runtime?: any; include_post_analysis?: boolean }) =>
    fetchJSON<{ run_id: string; task: Task }>(`/tasks/${id}/run`, {
      method: 'POST',
      body: JSON.stringify(overrides || {}),
    }),
  pauseTask: (id: string) =>
    fetchJSON<{ status: string }>(`/tasks/${id}/pause`, { method: 'POST' }),
  resumeTask: (id: string) =>
    fetchJSON<{ status: string }>(`/tasks/${id}/resume`, { method: 'POST' }),
  stopTask: (id: string) =>
    fetchJSON<{ status: string }>(`/tasks/${id}/stop`, { method: 'POST' }),

  // Run data
  getRunSummary: (runId: string) => fetchJSON<RunSummary>(`/pipeline/${runId}/summary`),
  getRunStatus: (runId: string) =>
    fetchJSON<{ log: LogEntry[]; last: LogEntry | null }>(`/pipeline/${runId}/status`),
  getGrowthList: (runId: string) => fetchJSON<GrowthList>(`/pipeline/${runId}/results`),
  getArtifact: (runId: string, name: string) => fetchJSON<any>(`/pipeline/${runId}/file/${name}`),

  // Stream (EventSource handled separately)
  getStreamUrl: (runId: string) => `${BASE_URL}/pipeline/${runId}/stream`,

  getActivity: (runId: string) => fetchJSON<any>(`/pipeline/${runId}/activity`),
  startActivity: (runId: string, action: string) => fetchJSON<any>(`/pipeline/${runId}/activity`, { method: 'POST', body: JSON.stringify({ action }) }),

  // Export
  getExportUrl: (runId: string) => `${BASE_URL}/pipeline/${runId}/export/xlsx`,
  createExportToken: (runId: string) =>
    fetchJSON<{ token: string }>(`/pipeline/${runId}/export-token`, { method: 'POST' }),
  downloadExportXlsx: async (runId: string, activity = 'all') => {
    const { token } = await api.createExportToken(runId)
    const link = document.createElement('a')
    link.href = `${BASE_URL}/pipeline/${runId}/export/xlsx?download_token=${encodeURIComponent(token)}&activity=${encodeURIComponent(activity)}`
    link.download = ''
    link.target = '_blank'
    link.rel = 'noopener'
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    link.remove()
  },
}
